import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material'
import { I18n, Translate } from 'common'
import { observer } from 'mobx-react-lite'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { ImageBypartAPIResponse } from 'src/api/searchResults/interfaces'
import { isMiscellaneousVehicle } from 'src/features/search/Results/utils/miscellaneousVehicle'
import { Vehicle } from 'src/store/models/Vehicles'
import { useUiStateStore } from 'src/store/uiState/UiStateStore'
import { UserAttributeKey } from 'src/store/user/interfaces'
import { useUserStore } from 'src/store/user/UserStore'
import { defaultTheme } from 'src/theme'
import { breakpoints } from 'src/theme/breakpoints'
import { Checkbox, Thumbnail } from 'src/ui-components'
import styled from 'styled-components'
import { ShipToDropdown } from './ShipToDropdown'
import { SellerWithLocations } from '../store/ISellerCart'
import { OrderFormTextInput } from './OrderFormTextInput'
import { SellerLocations } from './SellerLocations'
import { RootState, store } from 'store'
import { cartSlice } from '../store/cartStore'
import { useSelector } from 'react-redux'
import {
  getVehicleOrderFormData,
  shouldShowVehicleOrderFormError,
} from '../store/cartSelectors'
import { setCopyToAllCarts } from '../store/uiStore'
import { formatVehicleDisplay } from 'src/helpers/utils'

interface SellerInfoProps {
  seller: SellerWithLocations
  partImages: ImageBypartAPIResponse[]
  currentVehicle: Vehicle
  IsSamePoNumber: boolean
}

export const SellerInfo = observer(
  ({
    seller,
    partImages,
    currentVehicle,
    IsSamePoNumber,
  }: SellerInfoProps): ReactElement => {
    const userStore = useUserStore()
    const { isMultisellerUser, showYourCost, isDisplayMultiSellerLogos } =
      useUiStateStore()

    const noteRequired = userStore?.preferences?.cart_noteRequired === 'true'
    const noteMaxSize = userStore?.preferences?.cart_noteMaxSize

    const displayShipToAddressInCartPage =
      userStore.getUserAttribute(
        UserAttributeKey.displayShipToAddressInCartPage
      ) === 'true'

    const [showAdditionalOptions, setShowAdditionalOptions] =
      useState<boolean>(false)

    const poRequired = userStore?.preferences?.cart_poRequired === 'true'
    const poMaxSize = userStore?.preferences?.cart_poMaxSize
    const displayMultiSellerLogos =
      !seller?.coverageKey || isDisplayMultiSellerLogos()

    const handleAdditionalOptionsClick = () => {
      setShowAdditionalOptions(!showAdditionalOptions)
    }

    const showError: boolean = useSelector((state: RootState) => {
      return shouldShowVehicleOrderFormError(state, currentVehicle)
    })
    // when poNumber or notetostore are required expanding additional options component
    useEffect(() => {
      if (showError) {
        setShowAdditionalOptions(showError)
      }
    }, [showError])

    useEffect(() => {
      if (!isMultisellerUser()) {
        setShowAdditionalOptions(true)
      }
    }, [isMultisellerUser])

    const getVehicleString = useCallback(
      (vehicle: Vehicle): string => {
        if (isMiscellaneousVehicle(vehicle) || !vehicle.year) {
          return ''
        }

        return formatVehicleDisplay(vehicle).substring(0, Number(noteMaxSize))
      },
      [noteMaxSize]
    )

    const cartNoteHasVehicle: boolean =
      userStore?.preferences[UserAttributeKey.cartNoteHasVehicle] === 'true'

    const { poNumber, noteToStore } = useSelector((state: RootState) => {
      return getVehicleOrderFormData(state, currentVehicle)
    })

    const { isCopyToAllCarts, commonOrderFormData } = useSelector(
      (state: RootState) => {
        return {
          isCopyToAllCarts: state.ui.copyToAllCarts,
          commonOrderFormData: state.cart.commonOrderFormData,
        }
      }
    )

    // Initialize seller's noteToStore with default vehicle string if empty and cartNoteHasVehicle is enabled
    useEffect(() => {
      if (cartNoteHasVehicle && !seller.noteToStore) {
        const formDataHasNoNoteToStore =
          cartNoteHasVehicle && getVehicleString(currentVehicle)
            ? getVehicleString(currentVehicle)
            : ''

        store.dispatch(
          cartSlice.actions.setFieldOnSeller({
            field: 'noteToStore',
            vehicle: currentVehicle,
            seller,
            value: formDataHasNoNoteToStore,
          })
        )
      }
    }, [cartNoteHasVehicle, seller, currentVehicle, getVehicleString])

    useEffect(() => {
      if (isCopyToAllCarts) {
        store.dispatch(
          cartSlice.actions.setFieldOnSeller({
            field: 'poNumber',
            vehicle: currentVehicle,
            seller,
            value: commonOrderFormData.poNumber,
          })
        )
        store.dispatch(
          cartSlice.actions.setFieldOnSeller({
            field: 'noteToStore',
            vehicle: currentVehicle,
            seller,
            value: commonOrderFormData.noteToStore,
          })
        )
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCopyToAllCarts])

    return (
      <SellerInfoWrapper key={seller.coverageKey}>
        {isMultisellerUser() && (
          <SellerHeader>
            <SellerTitleWrapper>
              {displayMultiSellerLogos && (
                <Thumbnail
                  urlOrBase64={
                    seller.coverageId === 0
                      ? userStore.userLogo
                      : seller.logoUrl
                  }
                  width={100}
                  height={36}
                  alt={`${seller.sellerName} logo`}
                  placeholder
                />
              )}
              <StyledItemsTitle
                style={{
                  paddingTop: '5px',
                  paddingLeft: 0,
                  paddingBottom: 0,
                  marginLeft: displayMultiSellerLogos ? '20px' : '0px',
                }}
              >
                <I18n
                  name="sellerOrder"
                  textReplacement={[seller.sellerName]}
                />
              </StyledItemsTitle>
            </SellerTitleWrapper>
            <LocalLink onClick={handleAdditionalOptionsClick}>
              <I18n
                name="additionalOrderOptions"
                style={{ fontSize: '14px' }}
              />
              {showAdditionalOptions ? <ArrowDropUp /> : <ArrowDropDown />}
            </LocalLink>
          </SellerHeader>
        )}

        {showAdditionalOptions && (
          <FormContainer isMultisellerUser={isMultisellerUser()}>
            <OrderFormTextInput
              width="228px"
              label="poNumber"
              onChange={(value) =>
                store.dispatch(
                  cartSlice.actions.setFieldOnSeller({
                    field: 'poNumber',
                    vehicle: currentVehicle,
                    seller,
                    value,
                  })
                )
              }
              value={seller.poNumber ? seller.poNumber : poNumber}
              disabled={IsSamePoNumber}
              noMargin
              required={poRequired}
              showMaxSize
              maxSize={Number(poMaxSize)}
              showError={showError}
              error={
                showError &&
                poRequired &&
                (seller.poNumber ? seller.poNumber : poNumber) === ''
              }
            />
            <OrderFormTextInput
              width="450px"
              label="noteToStore"
              onChange={(value) =>
                store.dispatch(
                  cartSlice.actions.setFieldOnSeller({
                    field: 'noteToStore',
                    vehicle: currentVehicle,
                    seller,
                    value,
                  })
                )
              }
              value={seller.noteToStore ? seller.noteToStore : noteToStore}
              showMaxSize
              maxSize={Number(noteMaxSize)}
              required={noteRequired}
              showError={showError}
              disabled={false}
              noMargin
              error={
                showError &&
                noteRequired &&
                (seller.noteToStore ? seller.noteToStore : noteToStore) === ''
              }
            />
            {displayShipToAddressInCartPage && seller.coverageKey === '' && (
              <ShipToDropdown
                vehicle={currentVehicle}
                onSelect={(address: string) =>
                  store.dispatch(
                    cartSlice.actions.setFieldOnVehicleCart({
                      field: 'shipToAddress',
                      vehicle: currentVehicle,
                      value: address,
                    })
                  )
                }
              />
            )}
          </FormContainer>
        )}
        {!isMultisellerUser() && (
          <div style={{ marginTop: '15px', marginLeft: '12px' }}>
            <Checkbox
              value={1}
              checkOutlinedIconStyle={{
                fontSize: 12,
                stroke: defaultTheme.colors.border,
                strokeWidth: '1',
              }}
              labelStyle={{
                fontFamily: 'Roboto',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: 11,
                color: defaultTheme.colors.almostBlack,
              }}
              label={Translate('copyToAllCarts')}
              active={isCopyToAllCarts}
              onClick={({ isChecked }) => {
                store.dispatch(setCopyToAllCarts(isChecked))

                if (isChecked) {
                  store.dispatch(
                    cartSlice.actions.setCommonOrderFormData({
                      poNumber: seller.poNumber ? seller.poNumber : poNumber,
                      noteToStore: seller.noteToStore,
                    })
                  )
                } else {
                  store.dispatch(
                    cartSlice.actions.setCommonOrderFormData({
                      poNumber: '',
                      noteToStore: '',
                    })
                  )
                }
              }}
            />
          </div>
        )}
        <StyledItemsLaborSpecsTitleWrapper>
          <StyledItemsTitle>
            <I18n name="products" />
          </StyledItemsTitle>
        </StyledItemsLaborSpecsTitleWrapper>

        <CartTableScroll>
          <CartTable>
            <HeaderRow>
              <tr>
                <ColHeader>
                  <I18n name="item" />
                </ColHeader>
                <ColHeader>&nbsp;</ColHeader>
                {showYourCost() && (
                  <ColHeader>
                    <I18n name="yourCost" />
                  </ColHeader>
                )}
                <ColHeader>
                  <I18n name="listPrice" />
                </ColHeader>
                <ColHeader>
                  <I18n name="availability" />
                </ColHeader>
                <ColHeader>
                  <I18n name="quantity" />
                </ColHeader>
                <ColHeader>
                  <I18n name="total" />
                </ColHeader>
                <ColHeader>&nbsp;</ColHeader>
              </tr>
            </HeaderRow>
            <tbody>
              <SellerLocations
                partImages={partImages}
                vehicle={currentVehicle}
                sellerDetails={seller}
              />
            </tbody>
          </CartTable>
        </CartTableScroll>
      </SellerInfoWrapper>
    )
  }
)

const SellerInfoWrapper = styled.div`
  border-bottom: 1px solid ${defaultTheme.colors.border};
  margin-top: 20px;
`

const SellerTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
`

const SellerHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-left: 15px;
`

export const CartSubtitle = styled.h4`
  font-family: 'Oswald', serif;
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 30px;
  color: ${defaultTheme.colors.text};
  mix-blend-mode: normal;
  margin: 0;
  text-align: left;
`

export const StyledItemsTitle = styled(CartSubtitle)`
  padding: 20px 14px;
  @media (max-width: ${breakpoints.mobileLarge}px) {
    padding: 22px 14px;
  }
`

export const StyledItemsLaborSpecsTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  background-color: ${defaultTheme.colors.white};
  color: ${defaultTheme.colors.primaryText};
  @media (max-width: ${breakpoints.mobileLarge}px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
`

export const CartTable = styled.table`
  width: 100%;

  @media (max-width: ${breakpoints.mobileLarge}px) {
    display: block;

    tbody {
      display: block;
      width: 100%;
    }
  }
`

export const CartTableScroll = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;

  @media (max-width: ${breakpoints.mobileLarge}px) {
    overflow-x: visible;
  }
`

export const HeaderRow = styled.thead`
  background-color: ${defaultTheme.colors.white};
  color: ${defaultTheme.colors.primaryText};
  border-bottom: 1px solid ${defaultTheme.colors.border};
  @media (max-width: ${breakpoints.mobileLarge}px) {
    display: none;
  }
`

export const ColHeader = styled.td`
  background-color: ${defaultTheme.colors.white};
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  padding: 20px 14px 20px 14px;
  text-align: left;
  width: 14%;
`

interface FormContainerProps {
  isMultisellerUser: boolean
}

const FormContainer = styled.div<FormContainerProps>`
  width: 100%;
  max-width: 100%;
  background-color: ${defaultTheme.colors.componentsBackground};
  overflow-x: hidden;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
  @media (max-width: ${breakpoints.tablet}px) {
    flex-direction: column;
    padding: 5px 0;
    margin-left: 0;
  }
  margin-top: ${(p) => (p.isMultisellerUser ? '40px' : '0px')};
  margin-left: 10px;
`

const LocalLink = styled.div`
  color: ${defaultTheme.colors.link};
  cursor: pointer;
  margin: 10px 10px 0 0;
`
